/**
 * AWE Shop The Look – Custom Element
 *
 * Standalone (no bundler imports) so it can be loaded via:
 *   <script src="{{ 'awe-shop-the-look.js' | asset_url }}" defer></script>
 *
 * Opens as a right-side drawer using the native <dialog> element.
 * Dispatches 'cart:update' after cart mutations so Horizon's cart-drawer
 * refreshes automatically (matches CartAddEvent.eventName = 'cart:update').
 *
 * Public event API (fire on document):
 *   new CustomEvent('awe:stl:open', { detail: { handles: [...], trigger: el } })
 */
(function () {
  'use strict';

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Escape user content before inserting as HTML */
  function escapeHtml(text) {
    const d = document.createElement('div');
    d.textContent = String(text ?? '');
    return d.innerHTML;
  }

  /** Return the CSS background value for a known material name */
  function getMaterialBackground(val) {
    const map = {
      '14K Gold Vermeil':              'radial-gradient(70.83% 50% at 50% 50%, #FCF1D5 0%, #EAD5A8 100%), #F8E3BB',
      '14k Gold Vermeil':              'radial-gradient(70.83% 50% at 50% 50%, #FCF1D5 0%, #EAD5A8 100%), #F8E3BB',
      'Brass/Gold Vermeil':            'radial-gradient(70.83% 50% at 50% 50%, #FCF1D5 0%, #EAD5A8 100%), #F8E3BB',
      '925 Sterling Silver':           'radial-gradient(50% 50% at 50% 50%, rgba(248,248,247,0.80) 0%, rgba(193,189,187,0.80) 100%), #E0DCD9',
      '14K Gold':                      'radial-gradient(70.83% 50% at 50% 50%, #FFF8E7 0%, #FFE6BA 100%), #FBE9C9',
      '14k Gold':                      'radial-gradient(70.83% 50% at 50% 50%, #FFF8E7 0%, #FFE6BA 100%), #FBE9C9',
      '14K Yellow Gold':               'radial-gradient(70.83% 50% at 50% 50%, #FFF8E7 0%, #FFE6BA 100%), #FBE9C9',
      '14k Yellow Gold':               'radial-gradient(70.83% 50% at 50% 50%, #FFF8E7 0%, #FFE6BA 100%), #FBE9C9',
      '14K White Gold':                'radial-gradient(50% 50% at 50% 50%, rgba(255,255,255,0.80) 0%, rgba(168,168,168,0.80) 100%), #E9E6E4',
      '14k White Gold':                'radial-gradient(50% 50% at 50% 50%, rgba(255,255,255,0.80) 0%, rgba(168,168,168,0.80) 100%), #E9E6E4',
      'Mixed Metals (Gold & Silver)':  'linear-gradient(225deg, rgba(115,115,115,0.80) -3.87%, rgba(255,253,253,0.80) 47.41%, rgba(255,224,157,0.80) 86.77%), #3E3E3E',
      'Gunmetal':                      'radial-gradient(50% 50% at 50% 50%, rgba(174,174,174,0.80) 0%, rgba(78,78,78,0.80) 100%), #706D6D',
      'Black Rhodium':                 'radial-gradient(50% 50% at 50% 50%, rgba(159,159,159,0.80) 0%, rgba(56,56,56,0.80) 100%), #382E28',
      'Oxidized 925 Sterling Silver':  'radial-gradient(50% 50% at 50% 50%, rgba(195,193,191,0.80) 0%, rgba(105,105,105,0.80) 100%), #696969',
      'Brass/Rhodium':                 'radial-gradient(50% 50% at 50% 50%, rgba(248,248,247,0.80) 0%, rgba(147,147,147,0.80) 100%), #7A7A7A',
    };
    return map[val] || '#cccccc';
  }

  /** Format Shopify money integer (cents) to currency string */
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  /** Derive variant display title from option values */
  function variantTitle(variant) {
    return [variant.option1, variant.option2, variant.option3]
      .filter(Boolean)
      .join(' / ');
  }

  /** Section IDs of every cart-items-component on the page (drawer, /cart). */
  function cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  /** Bag count as the header bubble shows it (snippets/cart-bubble.liquid): hidden lines left out. */
  function visibleItemCount(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      const props = item.properties || {};
      const hidden =
        props._awemembership === 'true' ||
        props._deluxe_gift === 'true' ||
        String(item.title || '').includes('Order Protection');
      return hidden ? sum : sum + item.quantity;
    }, 0);
  }

  /**
   * Notify Horizon's cart drawer and bag icon (ThemeEvents.cartUpdate) in the
   * shape their listeners expect: detail.data.sections lets cart-items-component
   * morph the drawer from the add response, detail.data.itemCount sets the bag
   * count, and detail.resource (the cart) spares every product form on the
   * page its own /cart.js fetch. A bare Event here had no detail, so those
   * listeners threw and the drawer kept its empty-bag markup from page load.
   * @param {Record<string, string>|undefined} sections - section HTML from the cart request
   */
  async function notifyCartUpdate(sections) {
    let cart = null;
    try {
      cart = await (await fetch('/cart.js')).json();
    } catch {
      /* the drawer still refreshes from sections; the bubble falls back to 0 */
    }
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || {},
          sourceId: 'awe-shop-the-look',
          data: {
            source: 'awe-shop-the-look',
            itemCount: visibleItemCount(cart),
            sections,
          },
        },
      })
    );
  }

  /** POST a cart request with the drawer's section HTML attached to the response. */
  async function postCart(url, payload) {
    const sectionIds = cartSectionIds();
    const body = sectionIds.length
      ? { ...payload, sections: sectionIds.join(','), sections_url: window.location.pathname }
      : payload;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  // ── Custom Element ──────────────────────────────────────────────────────────

  class AweShopTheLook extends HTMLElement {
    /** @type {HTMLDialogElement|null} */
    #dialog = null;

    /** @type {HTMLElement|null} – element that triggered the last open */
    #returnFocus = null;

    /** @type {Array<Object>} – product data fetched from /products/{handle}.js */
    #products = [];

    /** @type {Map<number, Object>} productId → selected variant object */
    #selectedVariants = new Map();

    /** @type {Set<number>} variant IDs confirmed in cart */
    #addedToCart = new Set();

    /** @type {Map<number, {variant, productId}>} variantId → details */
    #addedVariants = new Map();

    /** @type {string} interstitial_handle of the module that opened the current drawer session */
    #currentModuleHandle = '';

    connectedCallback() {
      this.#dialog = this.querySelector('dialog');
      if (!this.#dialog) return;

      // Singleton guard: if another instance is already active, skip registration.
      // This prevents double-drawer when multiple main-collection sections exist on the page.
      if (document.querySelector('awe-shop-the-look-component[data-stl-active]')) return;
      this.setAttribute('data-stl-active', '');

      // Listen for open requests from trigger buttons (event delegation)
      document.addEventListener('awe:stl:open', this.#onOpenRequest);

      // Static event bindings on the dialog itself
      this.#dialog.addEventListener('click', this.#onDialogClick);
      this.#dialog.addEventListener('keydown', this.#onKeyDown);
      this.#dialog.addEventListener('close', this.#onNativeClose);

      // Event delegation for dynamic product rows
      this.#dialog.addEventListener('click', this.#onProductAreaClick);
    }

    disconnectedCallback() {
      document.removeEventListener('awe:stl:open', this.#onOpenRequest);
    }

    // ── Open / Close ──────────────────────────────────────────────────────────

    #onOpenRequest = (event) => {
      const { handles, trigger } = event.detail ?? {};
      this.#returnFocus = trigger instanceof Element ? trigger : document.activeElement;
      this.#currentModuleHandle = trigger?.dataset?.moduleHandle || '';
      this.#openWithProducts(Array.isArray(handles) ? handles : []);
    };

    async #openWithProducts(handles) {
      if (!this.#dialog) return;

      this.#resetState();
      this.#showLoading(true);

      // Prevent body scroll (mirror Horizon's dialog behaviour)
      const scrollY = window.scrollY;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      if (!this.#dialog.open) {
        this.#dialog.showModal();
      }

      // Move initial focus to close button for a11y
      requestAnimationFrame(() => {
        this.querySelector('[data-awe-stl-close]')?.focus();
      });

      await this.#loadProducts(handles);
    }

    #close() {
      if (!this.#dialog?.open) return;

      // Restore body scroll first (before animation, so layout doesn't jump)
      const top = Math.abs(parseInt(document.body.style.top || '0', 10));
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top, behavior: 'instant' });

      // Return focus to trigger
      if (this.#returnFocus) {
        this.#returnFocus.focus();
        this.#returnFocus = null;
      }

      // Animate out, then actually close.
      // setTimeout fallback ensures dialog.close() fires even when
      // animationend is suppressed (prefers-reduced-motion, no animation played, etc.)
      this.#dialog.classList.add('awe-stl-dialog--closing');

      const panel = this.#dialog.querySelector('.awe-stl-panel');
      let closed = false;
      const doClose = () => {
        if (closed) return;
        closed = true;
        this.#dialog.classList.remove('awe-stl-dialog--closing');
        this.#dialog.close();
      };

      // 300ms matches the 0.25s slide-out animation with a small buffer
      const fallback = setTimeout(doClose, 300);
      panel?.addEventListener('animationend', () => {
        clearTimeout(fallback);
        doClose();
      }, { once: true });
    }

    /** Called when dialog closes via any means (Esc, .close(), backdrop) */
    #onNativeClose = () => {
      // Safety net: restore scroll lock if #close() didn't already clear it
      // (e.g. native Escape before our event.preventDefault fix takes effect)
      if (document.body.style.position === 'fixed') {
        const top = Math.abs(parseInt(document.body.style.top || '0', 10));
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo({ top, behavior: 'instant' });
      }
      if (this.#returnFocus) {
        this.#returnFocus.focus();
        this.#returnFocus = null;
      }
    };

    // ── Event delegation ──────────────────────────────────────────────────────

    #onDialogClick = (event) => {
      // Click on backdrop (the <dialog> element itself outside the panel)
      if (event.target === this.#dialog) {
        this.#close();
      }
    };

    #onKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Prevent the browser's native dialog.close() so our animated #close()
        // is the sole code path. Without this, the browser closes the dialog
        // synchronously before our animationend listener can fire.
        event.preventDefault();
        this.#close();
      }
    };

    #onProductAreaClick = (event) => {
      const target = event.target;

      // Close button
      if (target.closest('[data-awe-stl-close]')) {
        this.#close();
        return;
      }

      // Checkout button
      if (target.closest('[data-awe-stl-checkout]')) {
        window.location.href = '/checkout';
        return;
      }

      // "More Options" → open sub-slide
      const moreBtn = target.closest('[data-awe-stl-more-options]');
      if (moreBtn) {
        const row = moreBtn.closest('.awe-stl-product');
        if (row) this.#openSlide(row);
        return;
      }

      // Back arrow in sub-slide
      const backBtn = target.closest('[data-awe-stl-back]');
      if (backBtn) {
        const row = backBtn.closest('.awe-stl-product');
        if (row) this.#closeSlide(row);
        return;
      }

      // Variant selection
      const optionBtn = target.closest('[data-awe-stl-option]');
      if (optionBtn) {
        const row = optionBtn.closest('.awe-stl-product');
        if (row) this.#handleVariantSelect(row, optionBtn);
        return;
      }

      // Add to cart (inline and options-slide)
      const addBtn = target.closest('[data-awe-stl-add]');
      if (addBtn && !addBtn.disabled) {
        const row = addBtn.closest('.awe-stl-product');
        if (row) this.#addToCart(row);
        return;
      }

      // Remove from cart
      const removeBtn = target.closest('[data-awe-stl-remove]');
      if (removeBtn) {
        const row = removeBtn.closest('.awe-stl-product');
        if (row) this.#removeFromCart(row, removeBtn);
        return;
      }

      // Incidental click on the product row itself (image/title/price), not
      // one of the controls handled above.
      const productMain = target.closest('[data-product-main]');
      if (productMain) {
        const row = productMain.closest('.awe-stl-product');
        if (row) this.#trackProductClick(row);
      }
    };

    /** Fires stl_product_click. GA4 event name for "user clicks a product inside an interstitial". */
    #trackProductClick(row) {
      const productId = parseInt(row.dataset.productId, 10);
      const product = this.#products.find((p) => p.id === productId);
      if (!window.aweFireGA4Event) return;
      window.aweFireGA4Event('stl_product_click', {
        interstitial_handle: this.#currentModuleHandle,
        product_handle: row.dataset.productHandle || '',
        product_name: product?.title || '',
      });
    }

    // ── Loading / State ───────────────────────────────────────────────────────

    #showLoading(show) {
      const loading = this.#dialog?.querySelector('[data-awe-stl-loading]');
      const products = this.#dialog?.querySelector('[data-awe-stl-products]');
      if (loading) loading.hidden = !show;
      if (products) products.innerHTML = show ? '' : products.innerHTML;
    }

    #resetState() {
      this.#products = [];
      this.#selectedVariants.clear();
      this.#addedToCart.clear();
      this.#addedVariants.clear();
      const products = this.#dialog?.querySelector('[data-awe-stl-products]');
      if (products) products.innerHTML = '';
    }

    // ── Product Fetching ──────────────────────────────────────────────────────

    async #loadProducts(handles) {
      if (!handles.length) {
        this.#showLoading(false);
        this.#showEmpty(true);
        return;
      }

      try {
        const results = await Promise.all(
          handles.map((handle) =>
            fetch(`/products/${handle}.js`)
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );

        const valid = results.filter(Boolean);
        this.#showLoading(false);

        if (!valid.length) {
          this.#showEmpty(true);
          return;
        }

        const container = this.#dialog?.querySelector('[data-awe-stl-products]');
        if (!container) return;

        for (const data of valid) {
          const firstAvailable = data.variants.find((v) => v.available) ?? data.variants[0];
          this.#products.push({
            id: data.id,
            handle: data.handle,
            title: data.title,
            options: data.options ?? [],
            variants: data.variants ?? [],
            tags: data.tags ?? [],
          });
          if (firstAvailable) {
            this.#selectedVariants.set(data.id, firstAvailable);
          }
          container.insertAdjacentHTML('beforeend', this.#renderProduct(data));
        }
      } catch {
        this.#showLoading(false);
        this.#showEmpty(true);
      }
    }

    #showEmpty(show) {
      const el = this.#dialog?.querySelector('[data-awe-stl-empty]');
      if (el) el.hidden = !show;
    }

    // ── Render ────────────────────────────────────────────────────────────────

    #renderProduct(data) {
      const variantCount = data.variants.length;
      const selected = data.variants.find((v) => v.available) ?? data.variants[0];
      const img = selected?.featured_image ?? data.featured_image;
      const imgSrc = img?.src ?? img ?? '';
      const imgAlt = img?.alt ?? data.title;

      const price = formatMoney(selected.price);
      const comparePrice =
        selected.compare_at_price > selected.price
          ? formatMoney(selected.compare_at_price)
          : '';
      const isAvailable = selected.available !== false;
      const addLabel = isAvailable ? 'Add to Bag' : 'Out of Stock';

      // Build variant option HTML for inline (≤2 variants) and options-slide
      let inlineOptionsHtml = '';
      let slideOptionsHtml = '';

      if (data.options?.length) {
        data.options.forEach((option, idx) => {
          const name = typeof option === 'string' ? option : option.name ?? `Option ${idx + 1}`;
          const nameLower = name.toLowerCase();
          const isMaterial = nameLower === 'material';
          const isColor = isMaterial || nameLower === 'color';
          const values = [
            ...new Set(
              data.variants.map((v) => [v.option1, v.option2, v.option3][idx]).filter(Boolean)
            ),
          ];

          const selectedVal = [selected.option1, selected.option2, selected.option3][idx];

          if (variantCount <= 2) {
            const btns = values
              .map((val) => {
                const bg = isMaterial ? getMaterialBackground(val) : '';
                return `
              <button
                type="button"
                class="awe-stl-option-btn${isColor ? ' awe-stl-option-btn--circle' : ''}${isMaterial ? ' awe-stl-option-btn--material' : ''}${val === selectedVal ? ' is-selected' : ''}"
                data-awe-stl-option
                data-option-name="${escapeHtml(nameLower)}"
                data-option-value="${escapeHtml(val)}"
                aria-label="${escapeHtml(name)}: ${escapeHtml(val)}"
                aria-pressed="${val === selectedVal ? 'true' : 'false'}"
                ${bg ? `style="background: ${bg};"` : ''}
              >${isMaterial ? '' : escapeHtml(val)}</button>`;
              })
              .join('');
            inlineOptionsHtml += `
              <div class="awe-stl-option-group">
                <span class="awe-stl-option-label">${escapeHtml(name)}</span>
                <div class="awe-stl-option-values">${btns}</div>
              </div>`;
          }

          const slideBtns = values
            .map((val) => {
              const bg = isMaterial ? getMaterialBackground(val) : '';
              return `
            <button
              type="button"
              class="awe-stl-option-slide-btn${isColor ? ' awe-stl-option-slide-btn--circle' : ''}${isMaterial ? ' awe-stl-option-slide-btn--material' : ''}${val === selectedVal ? ' is-selected' : ''}"
              data-awe-stl-option
              data-option-name="${escapeHtml(nameLower)}"
              data-option-value="${escapeHtml(val)}"
              aria-label="${escapeHtml(name)}: ${escapeHtml(val)}"
              aria-pressed="${val === selectedVal ? 'true' : 'false'}"
              ${bg ? `style="background: ${bg};"` : ''}
            >${isMaterial ? '' : escapeHtml(val)}</button>`;
            })
            .join('');
          slideOptionsHtml += `
            <div class="awe-stl-option-section">
              <p class="awe-stl-option-section-title">${escapeHtml(name)}</p>
              <div class="awe-stl-option-buttons">${slideBtns}</div>
            </div>`;
        });
      }

      return `
        <div class="awe-stl-product" data-product-id="${data.id}" data-product-handle="${escapeHtml(data.handle)}">
          <!-- Main product row -->
          <div class="awe-stl-product-main" data-product-main>
            <div class="awe-stl-product-image">
              ${imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="${escapeHtml(imgAlt)}" loading="lazy" width="92" height="118">` : ''}
            </div>
            <div class="awe-stl-product-info">
              <p class="awe-stl-product-title">${escapeHtml(data.title)}</p>
              <p class="awe-stl-product-variant-name" data-variant-name hidden></p>

              <div class="awe-stl-product-info-row">
                ${variantCount <= 2 ? `<div class="awe-stl-variants-inline">${inlineOptionsHtml}</div>` : ''}
                <div class="awe-stl-product-price" data-product-price>
                  ${comparePrice ? `<span class="awe-stl-price-compare">${comparePrice}</span>` : ''}
                  <span>${price}</span>
                </div>
              </div>

              <div class="awe-stl-cta-row">
                ${
                  variantCount > 2
                    ? `<button type="button" class="awe-stl-more-options-btn" data-awe-stl-more-options>More Options</button>`
                    : `<button
                        type="button"
                        class="awe-stl-add-btn${!isAvailable ? ' awe-stl-add-btn--oos' : ''}"
                        data-awe-stl-add
                        data-variant-id="${selected.id}"
                        ${!isAvailable ? 'disabled aria-disabled="true"' : ''}
                      >${addLabel}</button>
                      <button
                        type="button"
                        class="awe-stl-remove-btn"
                        data-awe-stl-remove
                        data-variant-id="${selected.id}"
                        aria-label="Remove from cart"
                        hidden
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                          <path d="M4.26 11.96C3.97 11.96 3.72 11.86 3.52 11.65C3.31 11.44 3.21 11.19 3.21 10.9V3.5H2.63V2.62H5.25V2.11H8.75V2.62H11.38V3.5H10.79V10.9C10.79 11.2 10.69 11.45 10.49 11.65C10.28 11.86 10.03 11.96 9.74 11.96H4.26ZM9.92 3.5H4.08V10.9C4.08 10.96 4.1 11 4.13 11.03C4.17 11.07 4.21 11.08 4.26 11.08H9.74C9.78 11.08 9.82 11.06 9.86 11.03C9.9 10.99 9.92 10.95 9.92 10.9V3.5ZM5.49 9.92H6.36V4.67H5.49V9.92ZM7.64 9.92H8.51V4.67H7.64V9.92Z" fill="currentColor"/>
                        </svg>
                      </button>`
                }
              </div>
            </div>
          </div>

          <!-- Options sub-slide (visible for any variant count when More Options is clicked) -->
          <div class="awe-stl-options-slide" data-options-slide aria-hidden="true">
            <div class="awe-stl-options-header">
              <button type="button" class="awe-stl-options-back" data-awe-stl-back aria-label="Back to products">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                  <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <h3 class="awe-stl-options-title">${escapeHtml(data.title)}</h3>
              <span class="awe-stl-options-spacer" aria-hidden="true"></span>
            </div>
            <div class="awe-stl-options-content">${slideOptionsHtml}</div>
            <div class="awe-stl-options-footer">
              <button
                type="button"
                class="awe-stl-options-add-btn${!isAvailable ? ' awe-stl-options-add-btn--oos' : ''}"
                data-awe-stl-add
                data-variant-id="${selected.id}"
                ${!isAvailable ? 'disabled aria-disabled="true"' : ''}
              >${addLabel}</button>
              <button
                type="button"
                class="awe-stl-remove-btn"
                data-awe-stl-remove
                data-variant-id="${selected.id}"
                aria-label="Remove from cart"
                hidden
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                  <path d="M4.26 11.96C3.97 11.96 3.72 11.86 3.52 11.65C3.31 11.44 3.21 11.19 3.21 10.9V3.5H2.63V2.62H5.25V2.11H8.75V2.62H11.38V3.5H10.79V10.9C10.79 11.2 10.69 11.45 10.49 11.65C10.28 11.86 10.03 11.96 9.74 11.96H4.26ZM9.92 3.5H4.08V10.9C4.08 10.96 4.1 11 4.13 11.03C4.17 11.07 4.21 11.08 4.26 11.08H9.74C9.78 11.08 9.82 11.06 9.86 11.03C9.9 10.99 9.92 10.95 9.92 10.9V3.5ZM5.49 9.92H6.36V4.67H5.49V9.92ZM7.64 9.92H8.51V4.67H7.64V9.92Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>
        </div>`;
    }

    // ── Variant Selection ─────────────────────────────────────────────────────

    #handleVariantSelect(row, clickedBtn) {
      const productId = parseInt(row.dataset.productId, 10);
      const product = this.#products.find((p) => p.id === productId);
      if (!product) return;

      const optionName = clickedBtn.dataset.optionName;
      const optionValue = clickedBtn.dataset.optionValue;

      // Update aria-pressed and visual state for all buttons of this option
      row.querySelectorAll(`[data-option-name="${optionName}"]`).forEach((btn) => {
        const isSelected = btn.dataset.optionValue === optionValue;
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      // Build option position map
      const optionPos = {};
      product.options.forEach((opt, i) => {
        const name = typeof opt === 'string' ? opt : opt.name ?? `option${i + 1}`;
        optionPos[name.toLowerCase()] = i + 1;
      });

      // Gather all current selections
      const sel = {};
      row.querySelectorAll('[data-awe-stl-option].is-selected').forEach((btn) => {
        const pos = optionPos[btn.dataset.optionName];
        if (pos) sel[`option${pos}`] = btn.dataset.optionValue;
      });

      const matched = product.variants.find((v) => {
        return (
          (!sel.option1 || v.option1 === sel.option1) &&
          (!sel.option2 || v.option2 === sel.option2) &&
          (!sel.option3 || v.option3 === sel.option3)
        );
      });

      if (matched) {
        this.#selectedVariants.set(productId, matched);
        this.#updateProductDisplay(row, productId, matched);
      }
    }

    #updateProductDisplay(row, productId, variant) {
      const product = this.#products.find((p) => p.id === productId);
      if (!product) return;

      const isAvailable = variant.available !== false;
      const variantId = variant.id;
      const isAdded = this.#addedToCart.has(variantId);

      // Price
      const priceEl = row.querySelector('[data-product-price]');
      if (priceEl) {
        const compare =
          variant.compare_at_price > variant.price
            ? `<span class="awe-stl-price-compare">${formatMoney(variant.compare_at_price)}</span>`
            : '';
        priceEl.innerHTML = `${compare}<span>${formatMoney(variant.price)}</span>`;
      }

      // Image
      if (variant.featured_image) {
        const imgEl = row.querySelector('.awe-stl-product-image img');
        if (imgEl) {
          const src = variant.featured_image.src ?? variant.featured_image;
          imgEl.src = src;
          imgEl.srcset = '';
        }
      }

      // Variant name (shown after add)
      const nameEl = row.querySelector('[data-variant-name]');
      if (nameEl) {
        if (isAdded && this.#addedVariants.has(variantId)) {
          nameEl.textContent = variantTitle(this.#addedVariants.get(variantId).variant);
          nameEl.hidden = false;
        } else {
          nameEl.hidden = true;
        }
      }

      // Add buttons
      row.querySelectorAll('[data-awe-stl-add]').forEach((btn) => {
        btn.dataset.variantId = variantId;
        btn.classList.remove('is-added', 'is-loading');

        if (!isAvailable) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.textContent = 'Out of Stock';
        } else if (isAdded) {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.classList.add('is-added');
          btn.textContent = 'Added';
        } else {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.textContent = 'Add to Bag';
        }
      });

      // Remove buttons
      row.querySelectorAll('[data-awe-stl-remove]').forEach((btn) => {
        btn.dataset.variantId = variantId;
        btn.hidden = !isAdded;
        btn.classList.toggle('is-visible', isAdded);
      });

      this.#syncProductAddedClass(row, productId);
    }

    #openSlide(row) {
      row.classList.add('is-slide-open');
      const slide = row.querySelector('[data-options-slide]');
      slide?.removeAttribute('aria-hidden');
      // Focus first button in the slide
      row.querySelector('[data-awe-stl-back]')?.focus();
    }

    #closeSlide(row) {
      row.classList.remove('is-slide-open');
      const slide = row.querySelector('[data-options-slide]');
      slide?.setAttribute('aria-hidden', 'true');
      // Return focus to More Options button
      row.querySelector('[data-awe-stl-more-options]')?.focus();
    }

    // ── Cart Add ──────────────────────────────────────────────────────────────

    async #addToCart(row) {
      const productId = parseInt(row.dataset.productId, 10);
      const variant = this.#selectedVariants.get(productId);
      if (!variant || !variant.available) return;

      const addBtns = [...row.querySelectorAll('[data-awe-stl-add]')];
      addBtns.forEach((b) => {
        b.disabled = true;
        b.classList.add('is-loading');
      });

      try {
        const product = this.#products.find((p) => p.id === productId);
        const isFinalSale = product?.tags?.some((t) => t === 'Final Sale' || t === 'Final  Sale') ?? false;
        // _stl_interstitial line-item property lets the interstitial that drove this
        // add-to-cart be attributed at the order level (GA4 "purchase attribution").
        const properties = {
          ...(isFinalSale ? { Purchase: 'Final Sale' } : {}),
          ...(this.#currentModuleHandle ? { _stl_interstitial: this.#currentModuleHandle } : {}),
        };
        const productLine = { id: variant.id, quantity: 1, ...(Object.keys(properties).length ? { properties } : {}) };

        // Same as the buy box (B04): add the Goddess Circle line in this request
        // when the shopper will be a member, so the drawer opens at member
        // prices. If that's refused, add the product on its own.
        const membershipLine = window.AweMembership?.lineForAdd?.(variant.id) ?? null;
        let data = membershipLine
          ? await postCart('/cart/add.js', { items: [productLine, membershipLine] }).catch(() => null)
          : null;
        if (!data || data.status) {
          data = await postCart('/cart/add.js', { items: [productLine] });
        }

        if (data.status) {
          // Shopify error response
          addBtns.forEach((b) => {
            b.disabled = false;
            b.classList.remove('is-loading');
          });
          return;
        }

        // Success
        this.#addedToCart.add(variant.id);
        if (product) this.#addedVariants.set(variant.id, { variant, productId });

        addBtns.forEach((b) => {
          b.disabled = false;
          b.classList.remove('is-loading');
          b.classList.add('is-added');
          b.textContent = 'Added';
        });

        // Variant name
        const nameEl = row.querySelector('[data-variant-name]');
        if (nameEl && product) {
          nameEl.textContent = variantTitle(variant);
          nameEl.hidden = false;
        }

        // Show remove buttons
        row.querySelectorAll('[data-awe-stl-remove]').forEach((btn) => {
          if (parseInt(btn.dataset.variantId, 10) === variant.id) {
            btn.hidden = false;
            btn.classList.add('is-visible');
          }
        });

        this.#syncProductAddedClass(row, productId);

        // Close options slide if open
        if (row.classList.contains('is-slide-open')) this.#closeSlide(row);

        if (window.aweFireGA4Event) {
          window.aweFireGA4Event('stl_add_to_cart', {
            interstitial_handle: this.#currentModuleHandle,
            product_handle: row.dataset.productHandle || '',
            product_name: product?.title || '',
          });
        }

        // Refresh Horizon cart-drawer and bubble count
        await notifyCartUpdate(data.sections);
      } catch {
        addBtns.forEach((b) => {
          b.disabled = false;
          b.classList.remove('is-loading');
        });
      }
    }

    // ── Cart Remove ───────────────────────────────────────────────────────────

    async #removeFromCart(row, removeBtn) {
      const variantId = parseInt(removeBtn.dataset.variantId, 10);
      const productId = parseInt(row.dataset.productId, 10);
      if (!variantId) return;

      removeBtn.disabled = true;

      try {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const lineItem = cart.items.find((item) => item.variant_id === variantId);

        if (!lineItem) {
          this.#updateAfterRemove(row, variantId, productId);
          removeBtn.disabled = false;
          return;
        }

        const result = await postCart('/cart/change.js', { id: lineItem.key, quantity: 0 });

        if (!result.status) {
          this.#updateAfterRemove(row, variantId, productId);
          await notifyCartUpdate(result.sections);
        }
      } catch {
        // swallow; UI stays consistent
      } finally {
        removeBtn.disabled = false;
      }
    }

    #updateAfterRemove(row, variantId, productId) {
      this.#addedToCart.delete(variantId);
      this.#addedVariants.delete(variantId);

      row.querySelectorAll('[data-awe-stl-remove]').forEach((btn) => {
        if (parseInt(btn.dataset.variantId, 10) === variantId) {
          btn.hidden = true;
          btn.classList.remove('is-visible');
        }
      });

      row.querySelectorAll('[data-awe-stl-add]').forEach((btn) => {
        if (parseInt(btn.dataset.variantId, 10) === variantId) {
          btn.classList.remove('is-added');
          btn.textContent = 'Add to Bag';
        }
      });

      const nameEl = row.querySelector('[data-variant-name]');
      if (nameEl) nameEl.hidden = true;

      this.#syncProductAddedClass(row, productId);
    }

    #syncProductAddedClass(row, productId) {
      const hasAdded = [...this.#addedToCart].some((vid) => {
        const d = this.#addedVariants.get(vid);
        return d && d.productId === productId;
      });
      row.classList.toggle('is-product-added', hasAdded);
    }
  }

  // ── Register ──────────────────────────────────────────────────────────────

  if (!customElements.get('awe-shop-the-look-component')) {
    customElements.define('awe-shop-the-look-component', AweShopTheLook);
  }

  // ── Click tracking ────────────────────────────────────────────────────────
  // Fires shop_the_look_clicked to GA4 and Klaviyo on every trigger click.
  // variant field reflects which arm of the Visually A/B test is active.

  function fireStlTracking(btn) {
    var payload = {
      page_type: btn.dataset.pageType || "collection",
      page_url:     window.location.href,
      module_id:    btn.dataset.moduleId    || '',
      module_index: btn.dataset.moduleIndex || '',
      variant:      btn.classList.contains('awe-stl-trigger--variant') ? 'variant' : 'control',
    };
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'shop_the_look_clicked', payload);
    }
    if (window._learnq && typeof window._learnq.push === 'function') {
      window._learnq.push(['track', 'Shop The Look Clicked', payload]);
    }
  }

  // ── Trigger button click delegation ───────────────────────────────────────
  // Wires [data-awe-stl-products] buttons to the awe:stl:open event.
  // Runs once here so awe-shop-the-look-trigger.liquid needs no inline <script>.

  if (window.__aweStlClickDelegationRegistered) return;
  window.__aweStlClickDelegationRegistered = true;

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-awe-stl-products]');
    if (!btn) return;
    event.preventDefault();
    fireStlTracking(btn);
    var raw = btn.dataset.aweStlProducts || '';
    var handles = raw.split(',').map(function (h) { return h.trim(); }).filter(Boolean);
    handles = handles.filter(function (h, i) { return handles.indexOf(h) === i; });
    if (!handles.length) return;
    document.dispatchEvent(
      new CustomEvent('awe:stl:open', {
        detail: { handles: handles, trigger: btn },
      })
    );
  });

  // ── Impression tracking ───────────────────────────────────────────────────
  // Fires stl_impression once per in-grid interstitial card. A MutationObserver
  // picks up cards added later by infinite scroll (results-list.js).
  // NOTE: interstitial_name has no dedicated field on the in_grid metaobject
  // today (only background_image/text/cta_url/cta_text/products) — this
  // defaults to the handle until a name field is added.

  (function () {
    var observedCards = new WeakSet();

    var impressionObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var card = entry.target;
          impressionObserver.unobserve(card);
          if (!window.aweFireGA4Event) return;
          var wrapper = card.closest('.awe-stl-in-grid-item');
          window.aweFireGA4Event('stl_impression', {
            interstitial_handle: card.dataset.bannerHandle || '',
            interstitial_name: card.dataset.bannerHandle || '',
            placement: (wrapper && wrapper.dataset.globalPosition) || '',
            collection_page: (wrapper && wrapper.dataset.collectionHandle) || '',
          });
        });
      },
      { threshold: 0.5 }
    );

    function observeNewCards(root) {
      root.querySelectorAll('.awe-stl-in-grid').forEach(function (card) {
        if (observedCards.has(card)) return;
        observedCards.add(card);
        impressionObserver.observe(card);
      });
    }

    function initImpressionTracking() {
      observeNewCards(document);

      var grid = document.querySelector('.product-grid-container');
      if (grid && window.MutationObserver) {
        new MutationObserver(function () {
          observeNewCards(grid);
        }).observe(grid, { childList: true, subtree: true });
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initImpressionTracking);
    } else {
      initImpressionTracking();
    }
  })();

  // ── Backwards-compatible global API (matches old window.openShopTheLook) ──

  window.openShopTheLook = function (handles, triggerEl) {
    var arr = Array.isArray(handles) ? handles : [handles];
    arr = arr.filter(function (h, i) { return arr.indexOf(h) === i; });
    document.dispatchEvent(
      new CustomEvent('awe:stl:open', {
        detail: { handles: arr, trigger: triggerEl ?? null },
      })
    );
  };
})();
